/**
 * Web (portal) push *mechanics* — the half of push that a browser tab does
 * differently from an MV3 extension worker.
 *
 * The policy this feeds (`derivePushSetupStatus`, prompt/dismissal rules, the
 * `PushDriver` shape) lives in `./push-setup` and is shared with the extension.
 * Only this file imports the window build of `firebase/messaging`, so nothing
 * that merely needs the status machine pays for it.
 */

import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  type MessagePayload,
  onMessage,
} from 'firebase/messaging';
import { firebaseConfig } from './firebase-config';
import type { RegisterablePushPlatform } from './notification-routes';
import type { PushDriver } from './push-setup';
import { trpcClient } from './trpc';

/**
 * Deliberately not `./firebase`, which calls `getAuth()` at import time: both
 * portals sign in with Better Auth now, so importing that here would pull the
 * whole auth SDK into the bundle purely to send a push token. Initialise the app
 * lazily instead, reusing whatever instance already exists.
 */
function messagingApp() {
  return getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
}

const TOKEN_KEY = 'pushDeviceToken';
const DISMISS_KEY = 'pushPromptDismissed';

function vapidKey(): string {
  return import.meta.env.VITE_FCM_VAPID_PUBLIC_KEY ?? '';
}

/**
 * The service worker is a static file in `public/`, so it cannot read the app's
 * env at build time. The Firebase web config is already public in the client
 * bundle, so passing it on the query string leaks nothing that isn't shipped
 * anyway — and it keeps the worker free of a build step.
 */
function serviceWorkerUrl() {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey ?? '',
    authDomain: firebaseConfig.authDomain ?? '',
    projectId: firebaseConfig.projectId ?? '',
    storageBucket: firebaseConfig.storageBucket ?? '',
    messagingSenderId: firebaseConfig.messagingSenderId ?? '',
    appId: firebaseConfig.appId ?? '',
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

async function messagingRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Scope is pinned to the origin root because a notification click has to be
    // able to focus or open any route in the app, not just one subtree.
    return await navigator.serviceWorker.register(serviceWorkerUrl(), { scope: '/' });
  } catch (error) {
    console.error('[push] service worker registration failed', error);
    return null;
  }
}

export function createWebPushDriver(platform: RegisterablePushPlatform): PushDriver {
  return {
    async register() {
      if (!(await isSupported())) throw new Error('Push is not supported in this browser');
      if (!vapidKey()) throw new Error('Push is not configured for this environment');
      if (typeof Notification === 'undefined') {
        throw new Error('Desktop notifications are not supported');
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Desktop notification permission was not granted');
      }

      const registration = await messagingRegistration();
      if (!registration) throw new Error('Unable to register the notification service worker');

      const token = await getToken(getMessaging(messagingApp()), {
        vapidKey: vapidKey(),
        serviceWorkerRegistration: registration,
      });
      if (!token) throw new Error('Unable to obtain a push token for this browser');

      await trpcClient.notifications.registerPushDevice.mutate({ token, platform });
      localStorage.setItem(TOKEN_KEY, token);
      return token;
    },

    async unregister() {
      const token = localStorage.getItem(TOKEN_KEY);
      // Deliberately more thorough than the extension's disablePush, which leaves
      // the device row active and lets dead tokens accumulate (a known P2 there).
      // A new implementation should not inherit that.
      if (token) {
        await trpcClient.notifications.unregisterPushDevice
          .mutate({ token })
          .catch((error) => console.error('[push] unregister failed', error));
      }
      localStorage.removeItem(TOKEN_KEY);
      if (await isSupported().catch(() => false)) {
        await deleteToken(getMessaging(messagingApp())).catch((error) =>
          console.error('[push] deleteToken failed', error)
        );
      }
    },

    async diagnostics() {
      const token = localStorage.getItem(TOKEN_KEY);
      const registration = await navigator.serviceWorker?.getRegistration('/').catch(() => null);
      return {
        supported: (await isSupported().catch(() => false)) && 'serviceWorker' in navigator,
        vapidKeyConfigured: !!vapidKey(),
        projectId: firebaseConfig.projectId ?? '',
        notificationPermission:
          typeof Notification === 'undefined'
            ? 'unavailable'
            : (Notification.permission ?? 'unreadable'),
        pushSubscribed: !!(await registration?.pushManager.getSubscription().catch(() => null)),
        tokenStored: !!token,
        tokenTail: token ? token.slice(-8) : null,
      };
    },

    async readDismissals() {
      try {
        return JSON.parse(localStorage.getItem(DISMISS_KEY) ?? '{}');
      } catch {
        return {};
      }
    },

    async writeDismissals(record) {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(record));
    },
  };
}

/**
 * Foreground messages never reach the service worker, so without this an arrival
 * while the tab is focused is completely silent — the OS toast is suppressed and
 * nothing replaces it.
 */
export function subscribeToForegroundPush(onPush: (payload: MessagePayload) => void) {
  let unsubscribe = () => {};
  void isSupported()
    .then((supported) => {
      if (supported) unsubscribe = onMessage(getMessaging(messagingApp()), onPush);
    })
    .catch(() => {});
  return () => unsubscribe();
}
